import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    UntypedFormArray,
    ValidationErrors,
    Validators,
    ValidatorFn,
} from '@angular/forms';
import {
    MatLegacyDialog as MatDialog,
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IGetApiResponse } from 'v2/modules/shared/interfaces/api-response.interface';
import { IIdName } from 'v2/modules/shared/interfaces/common.interface';
import { IRuleCustomFieldValue } from 'v2/modules/shared/interfaces/custom-field.interface';
import { EFieldType } from 'v2/modules/shared/interfaces/field-type.interface';
import { EComparisonOperators } from 'v2/modules/shared/interfaces/operator.interface';
import { AutomationBuilderApiService } from 'v2/modules/shared/services/api/automation-builder-api.service';
import { CustomFieldApiService } from 'v2/modules/shared/services/api/custom-field-api.service';
import { UserGroupsApiService } from 'v2/modules/shared/services/api/user-groups-api.service';
import { trackById } from 'v2/modules/shared/util';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { IUserGroup } from 'v2/modules/user-groups/interfaces/user-groups.interface';

import { AutomationRuleConfig } from '../../config/automation-rule.config';
import {
    EAutomationRuleActions,
    EAutomationRuleEvents,
    EAutomationRuleConditions,
    EAutomationRuleActionTypes,
    EAutomationRuleConditionTypes,
    IAutomationRule,
} from '../../interfaces/automation-rule.interface';

interface DataInterface {
  rule :IAutomationRule;
  ruleIds :{ id :number; name :string }[];
}

@Component({
  selector: 'lum-automation-rule-form',
  templateUrl: './automation-rule-form.component.html',
  styleUrls: ['./automation-rule-form.component.scss'],
})
export class AutomationRuleFormComponent implements OnInit, OnDestroy {
  readonly #destroy$ = new Subject<void>();

  readonly trackById = trackById;

  form :UntypedFormGroup;
  formErrors :string[] = [];
  automationRuleConfig = AutomationRuleConfig;
  eAutomationRuleEvents = EAutomationRuleEvents;
  customFields :IRuleCustomFieldValue[] = [];
  ruleIds :{ id :number; name :string }[] = [];
  submitting :boolean = false;
  hideConditionPanel :boolean = true;
  userGroups :IUserGroup[] = [];
  isAdvancedMode :boolean = false;
  jsonEditorForm :UntypedFormGroup;
  jsonEditorFormErrors :string[] = [];
  jsonValidationError :string | null = null;

  // Natural language properties
  naturalLanguageText :string = '';
  naturalLanguageProcessing :boolean = false;
  naturalLanguageError :string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data :DataInterface,
    private dialogRef :MatDialogRef<AutomationRuleFormComponent>,
    private fb :UntypedFormBuilder,
    private automationBuilderApiService :AutomationBuilderApiService,
    private customFieldApiService :CustomFieldApiService,
    private snackbar :MatSnackBar,
    private userGroupsApiService :UserGroupsApiService,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    // Initialize forms before anything else
    this.initForm();
    this.initJsonEditorForm();

    // Filter actions if event is 'receiving' after form is initialized
    this.filterActionsForReceivingEvent();

    // Then initialize other form-dependent components
    if (this.form) {
      this.#loadUserGroups();
      this.loadCustomFields();
      this.#listenEventChanges();
      // Initial check for hideConditionPanel
      this.#updateHideConditionPanel(this.form.get('event')?.value);

      // Listen for event changes to filter actions dynamically
      this.form
        .get('event')
        ?.valueChanges.pipe(takeUntil(this.#destroy$))
        .subscribe(() => {
          this.filterActionsForReceivingEvent();
        });
    } else {
      console.error('Form initialization failed');
    }
  }

  ngOnDestroy() :void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  /**
   * Generates a rule from natural language text
   * Sends the text to the API and populates the form with the response
   */
  generateRuleFromText() :void {
    if (!this.naturalLanguageText || this.naturalLanguageText.trim() === '') {
      this.naturalLanguageError = 'Please enter a description of your rule';

      return;
    }

    this.naturalLanguageProcessing = true;
    this.naturalLanguageError = null;

    this.automationBuilderApiService
      .textToRule(this.naturalLanguageText)
      .pipe(
        takeUntil(this.#destroy$),
        finalize(() => (this.naturalLanguageProcessing = false)),
      )
      .subscribe({
        next: (response) => {
          if (response.status === 'success' && response.jsonrule) {
            // Show confirmation dialog
            this.#showRuleConfirmationDialog(response.jsonrule);
          } else {
            this.naturalLanguageError =
              response.message || 'Failed to generate rule';
          }
        },
        error: (error) => {
          console.error('Error generating rule:', error);
          this.naturalLanguageError =
            error.error?.message || 'Failed to generate rule. Please try again.';
        },
      });
  }

  /**
   * Shows a confirmation dialog asking the user if they want to use the generated rule
   */
  #showRuleConfirmationDialog(jsonrule :any) :void {
    // Format the JSON for display
    const formattedJson = JSON.stringify(jsonrule, null, 2);

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Generated Rule',
          message: `Here is the generated rule: \n\n${formattedJson}\n\nDoes it look correct?`,
          // The component doesn't explicitly handle detail/isCode props
          // Just include in the message
        },
        width: DialogConfig.sizes.large,
        maxHeight: 'calc(100vh - 150px)',
      })
      .afterClosed()
      .pipe(takeUntil(this.#destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          // User confirmed, apply the rule
          this.#populateFormFromGeneratedRule(jsonrule);

          // Show success message
          this.snackbar.open('Rule applied successfully', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      });
  }

  /**
   * Populates the form with data from the generated rule
   */
  #populateFormFromGeneratedRule(jsonrule :any) :void {
    // If we're in advanced mode, directly update the JSON editor
    if (this.isAdvancedMode && this.jsonEditorForm) {
      // Set the raw jsonrule in the editor
      this.jsonEditorForm.patchValue({
        jsonRule: JSON.stringify({ jsonrule }, null, 2),
      });

      // No need to process form controls in advanced mode
      return;
    }

    // For standard form mode, process the rule

    // Clear existing form arrays
    if (this.form.get('conditions') && this.form.get('actions')) {
      while ((this.form.get('conditions') as UntypedFormArray).length > 0) {
        (this.form.get('conditions') as UntypedFormArray).removeAt(0);
      }

      while ((this.form.get('actions') as UntypedFormArray).length > 0) {
        (this.form.get('actions') as UntypedFormArray).removeAt(0);
      }
    }

    // Set basic form values
    this.form.patchValue({
      name: jsonrule.name || '',
      event: jsonrule.trigger || '',
    });

    // Process conditions for standard form view
    if (jsonrule.conditions && jsonrule.conditions.length > 0) {
      const conditionsArray = this.form.get('conditions') as UntypedFormArray;

      jsonrule.conditions.forEach((condition :any) => {
        // Find the matching condition in our config
        const conditionConfig = this.automationRuleConfig.conditions.find(
          (c :any) => c.type === condition.type,
        );

        if (conditionConfig) {
          // Add the condition to the form array
          const conditionFormGroup = this.#createConditionFormGroup(
            conditionConfig.id,
            condition,
          );
          conditionsArray.push(conditionFormGroup);
        }
      });
    }

    // Process actions for standard form view
    if (jsonrule.actions && jsonrule.actions.length > 0) {
      const actionsArray = this.form.get('actions') as UntypedFormArray;

      jsonrule.actions.forEach((action :any) => {
        // Find the matching action in our config
        const actionConfig = this.automationRuleConfig.actions.find(
          (a :any) => a.type === action.type,
        );

        if (actionConfig) {
          // Add the action to the form array
          const actionFormGroup = this.#createActionFormGroup(
            actionConfig.id,
            action,
          );
          actionsArray.push(actionFormGroup);
        }
      });
    }
  }

  /**
   * Creates a condition form group based on the condition type and value
   */
  #createConditionFormGroup(conditionId :string, condition :any) :UntypedFormGroup {
    // This is a simplified implementation
    // You would need to add specific handling for each condition type
    switch (conditionId) {
      case 'boolean':
        return this.fb.group({
          condition: [conditionId, Validators.required],
          boolean: this.fb.group({
            type: [condition.value.type || 'boolean', Validators.required],
            field: [condition.value.field, Validators.required],
            value: [condition.value.value, Validators.required],
          }),
        });

      case 'text':
        return this.fb.group({
          condition: [conditionId, Validators.required],
          text: this.fb.group({
            type: [condition.value.type || 'text', Validators.required],
            field: [condition.value.field, Validators.required],
            value: [condition.value.value, Validators.required],
            operator: [condition.value.operator || 'equals', Validators.required],
          }),
        });

      case 'hasTag':
        return this.fb.group({
          condition: [conditionId, Validators.required],
          hasTag: this.fb.group({
            type: [condition.value.type || 'has_tag', Validators.required],
            relationPath: [condition.value.relation || '.', Validators.required],
            tag: [condition.value.tag, Validators.required],
          }),
        });

      case EAutomationRuleConditionTypes.BOM_ITEM_RECEIVED:
        return this.fb.group({
          condition: [EAutomationRuleConditions.BOM_ITEM_RECEIVED, Validators.required],
          bomItemReceived: this.fb.group({
            type: [EAutomationRuleConditionTypes.BOM_ITEM_RECEIVED, Validators.required],
            value: this.fb.group(condition.value || {}),
          }),
        });

      // Add more cases for other condition types

      default:
        // For any condition types not specifically handled, create a basic group
        return this.fb.group({
          condition: [conditionId, Validators.required],
        });
    }
  }

  /**
   * Creates an action form group based on the action type and value
   */
  #createActionFormGroup(actionId :string, action :any) :UntypedFormGroup {
    // This is a simplified implementation
    // You would need to add specific handling for each action type
    switch (actionId) {
      case 'assignTag':
        return this.fb.group({
          action: [actionId, Validators.required],
          assignTag: this.fb.group({
            tags: this.fb.array(
              (action.value.tags || []).map((tag :any) => {
                return this.fb.control(tag);
              }),
            ),
          }),
        });

      case 'email':
        return this.fb.group({
          action: [actionId, Validators.required],
          email: this.fb.group({
            template: [action.value.template, Validators.required],
            email: [action.value.email, Validators.required],
          }),
        });

      case 'updateColumn':
        return this.fb.group({
          action: [actionId, Validators.required],
          updateColumn: this.fb.group({
            field: [action.value.field, Validators.required],
            customField: [
              action.value.field.includes('custom_field_')
                ? action.value.field
                : '',
            ],
            value: [action.value.value, Validators.required],
            type: [action.value.type || ''],
            options: [action.value.options || null],
            defaultOptions: [action.value.options || null],
          }),
        });

      case EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER:
        return this.fb.group({
          action: [EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER, Validators.required],
          completeAssemblyOrder: this.fb.group({
            type: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER],
            value: this.fb.group({
              value: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER]
            })
          }),
        });

      // Add more cases for other action types

      default:
        // For any action types not specifically handled, create a basic group
        return this.fb.group({
          action: [actionId, Validators.required],
        });
    }
  }

  onSubmit() :void {
    // Verify form is initialized
    if (!this.form && !this.jsonEditorForm) {
      return;
    }

    // Custom check for Receiving event
    const event = this.form.get('event')?.value;
    const conditionsArray = this.form.get('conditions') as UntypedFormArray;
    const actionsArray = this.form.get('actions') as UntypedFormArray;
    if (
      event === this.eAutomationRuleEvents.RECEIVING &&
      (conditionsArray.length === 0 || actionsArray.length === 0)
    ) {
      this.snackbar.open(
        'At least one condition and one action are required for Receiving rules.',
        'Close',
        { duration: SnackBarConfig.DEFAULT.duration }
      );
      return;
    }

    if (this.isAdvancedMode) {
      try {
        const jsonRuleValue = this.jsonEditorForm?.get('jsonRule')?.value;
        if (!jsonRuleValue) {
          this.jsonValidationError = 'Invalid or empty JSON data';

          return;
        }

        // Parse the JSON string from the form
        const jsonValue = JSON.parse(jsonRuleValue);

        // Validate the payload structure
        if (!jsonValue.jsonrule) {
          this.jsonValidationError = 'JSON must have a root "jsonrule" object';

          return;
        }

        this.submitRule(jsonValue);
      } catch (e) {
        this.jsonValidationError = 'Invalid JSON format';
        this.snackbar.open(
          'Invalid JSON format. Please check your input.',
          'Close',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
      }

      return;
    }

    if (this.form?.invalid) {
      return;
    }

    this.cleanForm();

    const { value } = this.form;
    const payload = {
      jsonrule: {
        name: value.name,
        trigger: value.event,
        conditions: value.conditions.map((data :any) => {
          const type = this.automationRuleConfig.conditions.find(
            (condition :IIdName) => condition.id === data.condition,
          )?.type;

          let conditionValue = data[data.condition];
          
          // Handle Has Not Performed Action condition with null ruleId (self-reference)
          if (data.condition === 'hasNotPerformedAction' && conditionValue?.ruleId === null) {
            // Create a copy without the ruleId property for self-reference
            conditionValue = { ...conditionValue };
            delete conditionValue.ruleId;
          }

          return {
            type,
            value: conditionValue,
          };
        }),
        actions: value.actions.map((data :any) => {
          const type = this.automationRuleConfig.actions.find(
            (action :IIdName) => action.id === data.action,
          )?.type;

          return {
            type,
            value: data[data.action],
          };
        }),
      },
    };

    this.submitRule(payload);
  }

  private submitRule(payload :any) :void {
    this.submitting = true;

    // If coming from advanced mode, we need to process the payload to match expected format
    if (this.isAdvancedMode) {
      // For advanced mode, we need to make sure the value properties are proper objects
      // and not stringified JSON
      if (payload.jsonrule?.conditions) {
        payload.jsonrule.conditions = payload.jsonrule.conditions.map(
          (condition :any) => {
            // If value is a string but contains JSON, parse it into an object
            if (condition.value && typeof condition.value === 'string') {
              try {
                // Parse string to convert it to a JSON object
                condition.value = JSON.parse(condition.value);
              } catch (e) {
                // If it's not valid JSON, just keep it as is
                console.error('Error parsing condition value:', e);
              }
            }

            return condition;
          },
        );
      }

      if (payload.jsonrule?.actions) {
        payload.jsonrule.actions = payload.jsonrule.actions.map((action :any) => {
          // If value is a string but contains JSON, parse it into an object
          if (action.value && typeof action.value === 'string') {
            try {
              // Parse string to convert it to a JSON object
              action.value = JSON.parse(action.value);
            } catch (e) {
              // If it's not valid JSON, just keep it as is
              console.error('Error parsing action value:', e);
            }
          }

          return action;
        });
      }
    }

    if (this.data?.rule?.id) {
      this.automationBuilderApiService
        .update({ rule: payload, id: this.data?.rule?.id })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: (rule) => {
            this.submitting = false;
            this.snackbar.open('Automation rule updated successfully', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(rule);
          },
          error: () => {
            this.submitting = false;
            this.snackbar.open(`Failed to update rule.`, 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } else {
      this.automationBuilderApiService
        .create(payload)
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: (rule) => {
            this.submitting = false;
            this.snackbar.open('Automation rule created successfully', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(rule);
          },
          error: () => {
            this.submitting = false;
            this.snackbar.open(`Failed to create rule.`, 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  private cleanForm() :void {
    for (let i = 0; i < this.form.controls['actions'].value.length; i++) {
      if (this.form.controls['actions'].value[i].action === 'updateColumn') {
        const field = this.form.controls['actions'].value[i].updateColumn['field'];
        const customField =
          field === 'customFields'
            ? this.form.controls['actions'].value[i].updateColumn['customField']
            : '';
        const type = this.form.controls['actions'].value[i].updateColumn['type'];
        let value = this.form.controls['actions'].value[i].updateColumn['value'];
        if (type === EFieldType.DROPDOWN && typeof value === 'object') {
          value = value['value'];
        }

        this.form.controls['actions'].value[i].updateColumn = {
          field: customField != '' ? customField : field,
          value: value,
        };
      }
    }
  }

  #loadUserGroups() :void {
    this.userGroupsApiService
      .get({})
      .pipe(takeUntil(this.#destroy$))
      .subscribe((response :IGetApiResponse) => {
        this.userGroups = response.data;
      });
  }

  private loadCustomFields() :void {
    this.customFieldApiService.get({ all: '1' }).subscribe({
      next: (response) => {
        this.customFields = response.data;
      },
      error: (error) => {
        this.snackbar.open(error, 'Fail', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
    });
  }

  #listenEventChanges() :void {
    if (!this.form) return;

    this.form
      .get('event')
      ?.valueChanges.pipe(takeUntil(this.#destroy$))
      .subscribe((value) => {
        this.#updateHideConditionPanel(value);
      });
  }

  #updateHideConditionPanel(eventValue :any) :void {
    if (!eventValue) return;

    this.hideConditionPanel = !(
      eventValue === this.eAutomationRuleEvents.DAILY ||
      eventValue === this.eAutomationRuleEvents.HOURLY
    );
  }

  private endDateValidator(group :UntypedFormGroup) :ValidationErrors | null {
    const operator = group.get('operator')?.value;
    const endControl = group.get('value.end');
    const end = endControl?.value;

    if (operator === 'between' && (!end || end === '')) {
      setTimeout(() => {
        endControl?.setValidators([Validators.required]);
        endControl?.updateValueAndValidity();
      }, 0);

      return null;
    }

    endControl?.clearValidators();
    endControl?.setErrors(null);

    return null;
  }

  get mode() :string {
    return this.data?.rule?.id ? 'Edit' : 'Create';
  }

  private initForm() :void {
    this.ruleIds = this.data?.ruleIds;
    this.form = this.fb.group({
      name: [this.data?.rule?.name || '', Validators.required],
      event: [this.data?.rule?.trigger || '', Validators.required],
      conditions: this.data?.rule?.conditions
        ? this.fb.array(
            this.data?.rule?.conditions?.map((condition :any) => {
              // Make sure we properly parse the condition value if it's a string
              let data = condition.value;
              if (typeof condition.value === 'string') {
                try {
                  data = JSON.parse(condition.value);
                } catch (e) {
                  console.warn(
                    `Unable to parse condition value: ${condition.value}`,
                    e,
                  );
                  // If parsing fails, use the original value
                  data = condition.value;
                }
              }

              switch (condition.type) {
                case 'boolean':
                  return this.fb.group({
                    condition: ['boolean', Validators.required],
                    boolean: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      field: [data.field, Validators.required],
                      value: [data.value, Validators.required],
                    }),
                  });

                case 'item_count':
                  return this.fb.group({
                    condition: ['itemCount', Validators.required],
                    itemCount: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      minCount: [data.minCount, Validators.required],
                    }),
                  });

                case 'text':
                  return this.fb.group({
                    condition: ['text', Validators.required],
                    text: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      field: [data.field, Validators.required],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                    }),
                  });

                case 'date':
                  return this.fb.group({
                    condition: ['date', Validators.required],
                    date: this.fb.group(
                      {
                        type: [
                          data.type || condition.type || '',
                          Validators.required,
                        ],
                        field: [data.field, Validators.required],
                        value: this.fb.group({
                          start: [data.value.start, Validators.required],
                          end: [data.value.end],
                        }),
                        operator: [data.operator, Validators.required],
                      },
                      { validators: this.endDateValidator },
                    ),
                  });

                case 'aggregate_relationship':
                  return this.fb.group({
                    condition: ['aggregateRelationship', Validators.required],
                    aggregateRelationship: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      relation: [data.relation, Validators.required],
                      aggregationType: [data.aggregationType, Validators.required],
                      attribute: [data.attribute, Validators.required],
                    }),
                  });

                case 'has_not_performed_action':
                  return this.fb.group({
                    condition: ['hasNotPerformedAction', Validators.required],
                    hasNotPerformedAction: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      condition: ['hasNotPerformedAction', Validators.required],
                      ruleId: [data.ruleId ?? null], // Use null if ruleId is missing (self-reference)
                    }),
                  });

                case 'has_tag':
                  return this.fb.group({
                    condition: ['hasTag', Validators.required],
                    hasTag: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      relationPath: [data.relationPath, Validators.required],
                      tag: [data.tag, Validators.required],
                    }),
                  });

                case 'order_age':
                  return this.fb.group({
                    condition: ['orderAge', Validators.required],
                    orderAge: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                    }),
                  });
                case 'order_status': {
                  const isOperatorForArrayType =
                    data.operator === EComparisonOperators.IN ||
                    data.operator === EComparisonOperators.NOT_IN;

                  return this.fb.group({
                    condition: ['orderStatus', Validators.required],
                    orderStatus: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                      selected: [
                        isOperatorForArrayType ? (data.selected ?? []) : [],
                      ],
                    }),
                  });
                }
                case 'shipping_method':
                  return this.fb.group({
                    condition: ['shippingMethod', Validators.required],
                    shippingMethod: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                    }),
                  });
                case 'product_sku':
                  return this.fb.group({
                    condition: ['productSku', Validators.required],
                    productSku: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                      selected: [data.selected ?? [], Validators.required],
                    }),
                  });
                case 'order_total':
                  return this.fb.group({
                    condition: ['orderTotal', Validators.required],
                    orderTotal: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                    }),
                  });
                case 'order_weight':
                  return this.fb.group({
                    condition: ['orderWeight', Validators.required],
                    orderWeight: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: this.fb.group({
                        units: [data.value.units, Validators.required],
                        value: [data.value.value],
                      }),
                      operator: [data.operator, Validators.required],
                    }),
                  });
                case 'approval_status':
                  return this.fb.group({
                    condition: ['approvalStatus', Validators.required],
                    approvalStatus: this.fb.group({
                      type: [
                        data.type || condition.type || '',
                        Validators.required,
                      ],
                      value: [data.value, Validators.required],
                      operator: [data.operator, Validators.required],
                    }),
                  });
                case EAutomationRuleConditionTypes.BOM_ITEM_RECEIVED:
                  return this.fb.group({
                    condition: [EAutomationRuleConditions.BOM_ITEM_RECEIVED, Validators.required],
                    bomItemReceived: this.fb.group({
                      type: [EAutomationRuleConditionTypes.BOM_ITEM_RECEIVED, Validators.required],
                      value: this.fb.group(data.value || {}),
                    }),
                  });
                default:
                  return new UntypedFormControl();
              }
            }),
          )
        : this.fb.array([]),
      actions: this.data?.rule?.actions
        ? this.fb.array(
            this.data?.rule?.actions?.map((action :any) => {
              // Make sure we properly parse the action value if it's a string
              let data = action.value;
              if (typeof action.value === 'string') {
                try {
                  data = JSON.parse(action.value);
                } catch (e) {
                  console.warn(`Unable to parse action value: ${action.value}`, e);
                  // If parsing fails, use the original value
                  data = action.value;
                }
              }

              switch (action.type) {
                case 'assign_tag':
                  return this.fb.group({
                    action: ['assignTag', Validators.required],
                    assignTag: this.fb.group({
                      tags: this.fb.array(
                        data.tags.map((tag :any) => {
                          return this.fb.control(tag);
                        }),
                      ),
                    }),
                  });

                case 'update_column':
                  return this.fb.group({
                    action: ['updateColumn', Validators.required],
                    updateColumn: this.fb.group({
                      field: [
                        data.field.includes('custom_field_')
                          ? 'customFields'
                          : data.field,
                        Validators.required,
                      ],
                      customField: [
                        data.field.includes('custom_field_') ? data.field : '',
                      ],
                      value: [data.value, Validators.required],
                      type: [data.type ?? ''],
                      options: [data.options ?? null],
                      defaultOptions: [data.options ?? null],
                    }),
                  });

                case 'aggregate_relationship':
                  return this.fb.group({
                    action: ['aggregateRelationship', Validators.required],
                    aggregateRelationship: this.fb.group({
                      relation: [data.relation, Validators.required],
                      aggregate: [data.aggregate, Validators.required],
                      attribute: [
                        data.attribute ? data.attribute : data.field,
                        Validators.required,
                      ],
                    }),
                  });

                case 'email_notification':
                  return this.fb.group({
                    action: ['email', Validators.required],
                    email: this.fb.group({
                      template: [data.template, Validators.required],
                      email: [data.email, Validators.required],
                    }),
                  });

                case 'hold_order':
                  return this.fb.group({
                    action: ['holdOrder', Validators.required],
                    holdOrder: this.fb.group({
                      holdType: [data.holdType, Validators.required],
                      holdUntil: [data.holdUntil, Validators.required],
                    }),
                  });

                case 'send_edi_855':
                  return this.fb.group({
                    action: [
                      EAutomationRuleActions.SEND_EDI_855,
                      Validators.required,
                    ],
                  });

                case 'send_unallocated_orders_in_purgatory_email':
                  return this.fb.group({
                    action: [
                      EAutomationRuleActions.SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL,
                      Validators.required,
                    ],
                    sendUnallocatedOrdersInPurgatoryEmail: this.fb.group({
                      userGroups: this.fb.array(
                        data?.userGroups?.map((userGroup :any) => {
                          return this.fb.control(userGroup);
                        }),
                      ),
                    }),
                  });

                case 'send_inbound_shipment_email':
                  return this.fb.group({
                    action: [
                      EAutomationRuleActions.SEND_INBOUND_SHIPMENT_EMAIL,
                      Validators.required,
                    ],
                    sendInboundShipmentEmail: this.fb.group({
                      userGroups: this.fb.array(
                        data?.userGroups?.map((userGroup :any) => {
                          return this.fb.control(userGroup);
                        }),
                      ),
                    }),
                  });

                case EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER:
                  return this.fb.group({
                    action: [EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER, Validators.required],
                    completeAssemblyOrder: this.fb.group({
                      type: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER],
                      value: this.fb.group({
                        value: [EAutomationRuleActionTypes.COMPLETE_ASSEMBLY_ORDER]
                      })
                    }),
                  });

                default:
                  return new UntypedFormControl();
              }
            }),
          )
        : this.fb.array([]),
    });

    this.#initErrorsSummary(this.form, 'formErrors');

    // Attach the custom validator to the actions FormArray after form creation
    const actionsArray = this.form.get('actions') as UntypedFormArray;
    actionsArray.setValidators(this.completeAssemblyOrderRequiredValidator());
    actionsArray.updateValueAndValidity();
  }

  private initJsonEditorForm() :void {
    let initialJson = '';

    if (this.data?.rule?.id) {
      // For editing, populate the JSON editor with the existing rule structure
      const rule = this.data.rule;

      // Process conditions and actions to make them properly nested JSON
      const processedConditions =
        rule.conditions?.map((condition :any) => {
          let valueObj = condition.value;
          // Try to parse the value if it's a string
          if (typeof condition.value === 'string') {
            try {
              valueObj = JSON.parse(condition.value);
            } catch (e) {
              // Keep as is if not parseable
              console.warn(
                `Unable to parse condition value: ${condition.value}`,
                e,
              );
            }
          }

          return {
            type: condition.type,
            value: valueObj,
          };
        }) || [];

      const processedActions =
        rule.actions?.map((action :any) => {
          let valueObj = action.value;
          // Try to parse the value if it's a string
          if (typeof action.value === 'string') {
            try {
              valueObj = JSON.parse(action.value);
            } catch (e) {
              // Keep as is if not parseable
              console.warn(`Unable to parse action value: ${action.value}`, e);
            }
          }

          return {
            type: action.type,
            value: valueObj,
          };
        }) || [];

      initialJson = JSON.stringify(
        {
          jsonrule: {
            name: rule.name,
            trigger: rule.trigger,
            conditions: processedConditions,
            actions: processedActions,
          },
        },
        null,
        2,
      );
    } else {
      // For new rules, create an empty template
      initialJson = JSON.stringify(
        {
          jsonrule: {
            name: '',
            trigger: '',
            conditions: [],
            actions: [],
          },
        },
        null,
        2,
      );
    }

    this.jsonEditorForm = this.fb.group({
      jsonRule: [initialJson, [Validators.required, this.jsonValidator]],
    });

    this.#initErrorsSummary(this.jsonEditorForm, 'jsonEditorFormErrors');
  }

  private jsonValidator(control :UntypedFormControl) :ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    try {
      JSON.parse(control.value);

      return null;
    } catch (e) {
      return { invalidJson: true };
    }
  }

  toggleAdvancedMode() :void {
    this.isAdvancedMode = !this.isAdvancedMode;

    if (this.isAdvancedMode && this.form.valid) {
      // Update JSON editor with current form values when switching to advanced mode
      const { value } = this.form;

      const processedConditions =
        value.conditions?.map((data :any) => {
          const type = this.automationRuleConfig.conditions.find(
            (condition :IIdName) => condition.id === data.condition,
          )?.type;

          // Extract the condition data based on the condition type
          const conditionData = data[data.condition];

          return {
            type,
            value: conditionData,
          };
        }) || [];

      const processedActions =
        value.actions?.map((data :any) => {
          const type = this.automationRuleConfig.actions.find(
            (action :IIdName) => action.id === data.action,
          )?.type;

          // Extract the action data based on the action type
          const actionData = data[data.action];

          return {
            type,
            value: actionData,
          };
        }) || [];

      const jsonValue = {
        jsonrule: {
          name: value.name,
          trigger: value.event,
          conditions: processedConditions,
          actions: processedActions,
        },
      };

      this.jsonEditorForm.patchValue({
        jsonRule: JSON.stringify(jsonValue, null, 2),
      });
    }
  }

  #getAllValidationErrors(
    formGroup :UntypedFormGroup | UntypedFormArray,
    parentKey :string = '',
  ) :string[] {
    let errors :string[] = [];

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      const controlKey = parentKey ? `${parentKey} > ${key}` : key;

      if (
        control instanceof UntypedFormGroup ||
        control instanceof UntypedFormArray
      ) {
        errors = errors.concat(this.#getAllValidationErrors(control, controlKey));
      } else if (control && control.invalid) {
        Object.keys(control.errors || {}).forEach((errorKey) => {
          let message = '';
          switch (errorKey) {
            case 'required':
              message = `${controlKey} is required.`;
              break;
            case 'email':
              message = `${controlKey} must be a valid email.`;
              break;
            case 'invalidJson':
              message = `${controlKey} contains invalid JSON.`;
              break;
            default:
              message = `${controlKey} is invalid (${errorKey}).`;
          }
          errors.push(message);
        });
      }
    });

    return errors;
  }

  #initErrorsSummary(
    form :UntypedFormGroup,
    errorsProperty :'formErrors' | 'jsonEditorFormErrors',
  ) :void {
    form.updateValueAndValidity();

    const _update = () =>
      (this[errorsProperty] = this.#getAllValidationErrors(form));

    _update();

    form.valueChanges.pipe(takeUntil(this.#destroy$)).subscribe(_update);
  }

  /**
   * Custom validator: If event is 'receiving', require completeAssemblyOrder action
   */
  private completeAssemblyOrderRequiredValidator() :ValidatorFn {
    return (formArray: UntypedFormArray) :ValidationErrors | null => {
      const parent = formArray.parent as UntypedFormGroup;
      if (!parent) {
        return null;
      } 

      const event = parent.get('event')?.value;
      if (event !== EAutomationRuleEvents.RECEIVING) {
        return null;
      }

      const hasCompleteAssemblyOrder = formArray.controls.some(
        (control) => control.get('action')?.value === EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER
      );
      return hasCompleteAssemblyOrder ? null : { completeAssemblyOrderRequired: true };
    };
  }

  /**
   * Filters actions to only keep 'completeAssemblyOrder' if event is 'receiving'
   */
  private filterActionsForReceivingEvent(): void {
    const event = this.form.get('event')?.value;
    const actionsArray = this.form.get('actions') as UntypedFormArray;
    if (event === EAutomationRuleEvents.RECEIVING && actionsArray) {
      const completeAssemblyOrderActions = actionsArray.controls.filter(
        control => control.get('action')?.value === EAutomationRuleActions.COMPLETE_ASSEMBLY_ORDER
      );
      actionsArray.clear();
      completeAssemblyOrderActions.forEach(control => actionsArray.push(control));
    }
  }
}
